import { Link } from "react-router-dom";
import { Header, Footer } from "../components/Layout";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import {
  CalendarClock, Clock, ShieldCheck, HeartHandshake, MessageSquareHeart, Sparkles,
  ArrowRight, CheckCircle2, Lock, Compass,
} from "lucide-react";

const expectations = [
  {
    icon: CalendarClock,
    title: "Meet at least once a month",
    body: "Consistency is everything. Commit to a minimum of one meeting per month with your mentee — in person, by video, phone, or however you both prefer. Regular touchpoints build the trust that makes mentorship work.",
  },
  {
    icon: Clock,
    title: "A one-year commitment",
    body: "Great mentoring relationships grow over time. We ask mentors to commit to at least one year so mentees get the stability and continuity they deserve as they grow into their careers.",
  },
  {
    icon: MessageSquareHeart,
    title: "Reasonable response times",
    body: "You don't need to be available 24/7 — just reliable. Aim to reply to your mentee within a few business days, and let them know your preferred way and hours to communicate.",
  },
  {
    icon: ShieldCheck,
    title: "Professionalism & respect",
    body: "Show up prepared, follow through on what you say you'll do, and treat your mentee as the capable professional they are. Model the workplace behavior you'd want them to carry forward.",
  },
  {
    icon: Lock,
    title: "Confidentiality",
    body: "What your mentee shares with you stays between you. If a safety concern ever arises, loop in the Aspiritech Academy team — otherwise, keep their trust.",
  },
];

const tips = [
  {
    icon: Compass,
    title: "Lead with curiosity, not fixing",
    body: "Ask what your mentee wants to work on before offering solutions. Their goals steer the relationship.",
  },
  {
    icon: HeartHandshake,
    title: "Be neurodivergent-affirming",
    body: "Be clear and literal, avoid unspoken expectations, and offer agendas ahead of meetings. Different communication styles are strengths, not problems to solve.",
  },
  {
    icon: Sparkles,
    title: "Celebrate small wins",
    body: "Career growth is made of small steps. Notice progress out loud — it builds confidence and momentum.",
  },
  {
    icon: MessageSquareHeart,
    title: "Set the rhythm together",
    body: "In your first meeting, agree on how often you'll meet, how you'll communicate, and what success looks like this year.",
  },
];

const MentorGuide = () => {
  return (
    <div className="min-h-screen flex flex-col bg-surface-app" data-testid="mentor-guide-page">
      <Header />
      <main className="flex-1">
        <section className="bg-gradient-to-br from-[#0F1B3D] via-[#1E3A8A] to-[#0A1A4A] text-white relative overflow-hidden">
          <div className="absolute top-0 right-0 w-[420px] h-[420px] bg-[#FFD507]/10 rounded-full blur-3xl pointer-events-none" />
          <div className="max-w-4xl mx-auto px-6 py-16 relative">
            <p className="text-[#FFD507] text-xs uppercase tracking-[0.25em] font-bold mb-3">Mentor Guide</p>
            <h1 className="text-3xl sm:text-5xl font-heading font-bold leading-tight">
              How to be a great mentor
            </h1>
            <p className="text-slate-300 mt-4 max-w-2xl">
              Mentoring at Aspiritech Academy is one of the most meaningful ways to shape a neurodivergent
              professional's career. Here's what we ask of our mentors — and how to do it well.
            </p>
          </div>
        </section>

        {/* Expectations */}
        <section className="max-w-4xl mx-auto px-6 py-12">
          <h2 className="text-2xl sm:text-3xl font-heading font-bold text-slate-900 mb-2">What we expect of mentors</h2>
          <p className="text-slate-600 mb-8">A few clear commitments that keep the relationship strong and dependable.</p>
          <div className="space-y-4">
            {expectations.map((e) => {
              const Icon = e.icon;
              return (
                <Card key={e.title} className="border border-slate-200">
                  <CardContent className="p-6 flex gap-4">
                    <div className="w-12 h-12 shrink-0 rounded-xl bg-[#1E3A8A]/10 flex items-center justify-center">
                      <Icon className="w-6 h-6 text-[#1E3A8A]" />
                    </div>
                    <div>
                      <h3 className="font-heading font-bold text-slate-900 text-lg">{e.title}</h3>
                      <p className="text-slate-600 mt-1 text-sm leading-relaxed">{e.body}</p>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </section>

        {/* Highlight band */}
        <section className="bg-[#FFD507] py-10">
          <div className="max-w-4xl mx-auto px-6 flex flex-col sm:flex-row items-center gap-4 text-[#0F1B3D]">
            <CalendarClock className="w-10 h-10 shrink-0" />
            <p className="font-heading text-lg sm:text-xl font-bold">
              The one non-negotiable: meet with your mentee at least once every month, for a minimum of one year.
            </p>
          </div>
        </section>

        {/* Tips */}
        <section className="max-w-4xl mx-auto px-6 py-12">
          <h2 className="text-2xl sm:text-3xl font-heading font-bold text-slate-900 mb-2">Tips for mentoring well</h2>
          <p className="text-slate-600 mb-8">Friendly, practical ideas to make your time together count.</p>
          <div className="grid sm:grid-cols-2 gap-5">
            {tips.map((t) => {
              const Icon = t.icon;
              return (
                <Card key={t.title} className="border border-slate-200 h-full">
                  <CardContent className="p-6">
                    <div className="w-11 h-11 rounded-xl bg-[#FFD507]/20 flex items-center justify-center mb-4">
                      <Icon className="w-5 h-5 text-[#0F1B3D]" />
                    </div>
                    <h3 className="font-heading font-bold text-slate-900">{t.title}</h3>
                    <p className="text-sm text-slate-600 mt-2 leading-relaxed">{t.body}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </section>

        {/* CTA */}
        <section className="max-w-4xl mx-auto px-6 pb-16">
          <Card className="overflow-hidden border-0 shadow-xl">
            <div className="bg-[#0F1B3D] text-white p-8 sm:p-10 flex flex-col sm:flex-row items-center justify-between gap-6">
              <div>
                <h3 className="text-2xl font-heading font-bold">Ready to make an impact?</h3>
                <p className="text-slate-300 mt-2">Create your mentor profile — it only takes a few minutes.</p>
                <ul className="mt-4 space-y-1.5 text-sm text-slate-300">
                  <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-[#FFD507] mt-0.5" /> Reviewed by our team before you go live</li>
                  <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-[#FFD507] mt-0.5" /> Matched with mentees in your field</li>
                </ul>
              </div>
              <Link to="/mentor-signup" data-testid="guide-apply-btn">
                <Button className="btn-accent h-12 px-7 text-base">
                  Apply to Mentor <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>
          </Card>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default MentorGuide;
